import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { Helmet } from 'react-helmet-async';
import { CYLINDO_ACCOUNT_ID } from 'commons/constants';
import buildCylindoFrameOneUrl from 'commons/buildCylindoFrameOneUrl';
import runAfterNextPaint from 'commons/runAfterNextPaint';
import avo from 'analytics/avo';
import usePDPGalleryProductProperties from 'data-hooks/useEvents/usePDPGalleryProductProperties';
import usePDPGalleryInteractedEvent from 'data-hooks/useEvents/usePDPGalleryInteractedEvent';
import { GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';
import usePdpId from '../state/context/usePdpId';
const SCRIPT_ID = 'cylindoScript';

// The viewer's unzoomed baseline, and how long its `zoom-change` stream must
// stay quiet before a zoom counts as finished
const UNZOOMED_LEVEL = 1;
const ZOOM_SETTLE_DELAY = 500;
const loadCylindo = callback => {
  const existingScript = document.getElementById(SCRIPT_ID);
  if (!existingScript) {
    const script = document.createElement('script');
    script.src = 'https://viewer-cdn.cylindo.com/v1/index.mjs';
    script.id = SCRIPT_ID;
    script.type = 'module';
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => {
      if (callback) callback();
    };
  } else if (callback) {
    callback();
  }
};
const PDPCylindo3dViewer = ({
  containerId = 'PDPCylindo3dViewer',
  cylindoFeatures,
  isMobile,
  rotateIndex,
  onViewerLoaded
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const controls = isMobile ? 'ar' : 'ar zoom';
  const productId = usePdpId();
  const productProperties = usePDPGalleryProductProperties();
  const {
    trackGalleryInteracted
  } = usePDPGalleryInteractedEvent();
  useDebounce(() => {
    if (productId && Object.keys(cylindoFeatures).length > 0) {
      loadCylindo(() => {
        // make sure the cylindo framework has been "installed"
        setIsReady(true);
      });
    }
  }, 100, [productId, cylindoFeatures]);
  useEffect(() => {
    if (!isMobile && containerRef.current && rotateIndex >= 0 && isReady) {
      const viewer = document.querySelector(`cylindo-viewer#${containerId}`);
      if (viewer && typeof viewer.animateToFrame === 'function') {
        if (rotateIndex === 0) {
          viewer.animateToFrame(1);
        } else if (rotateIndex === 1) {
          viewer.animateToFrame(4);
        } else if (rotateIndex === 2) {
          viewer.animateToFrame(9);
        } else if (rotateIndex === 3) {
          viewer.animateToFrame(14);
        } else if (rotateIndex === 4) {
          viewer.animateToFrame(17);
        }
      }
    }
  }, [containerRef, isMobile, rotateIndex, isReady, containerId]);

  // Assigning `features` runs Cylindo's reload synchronously (~40 ms at 4x CPU
  // slowdown), and a fabric click commits this effect before its paint. The
  // new frames load over the network anyway, so hand them over after the
  // paint; a newer selection cancels a pending one (CON-8270).
  useEffect(() => {
    if (!containerRef.current || !Object.keys(cylindoFeatures).length || !isReady) {
      return undefined;
    }
    let isCancelled = false;
    runAfterNextPaint(() => {
      if (!isCancelled && containerRef.current) {
        containerRef.current.features = cylindoFeatures;
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [containerRef, cylindoFeatures, isReady]);

  // Frame-1 fallback/placeholder image URL. Built via the shared builder so it stays
  // byte-identical to the server-resolved preload URL (CON-8293) - same URL means the
  // browser reuses the already-preloaded image and never double-fetches. The builder
  // encodes both feature key and value (carried over from develop's encoding fix on the
  // old inline param), so a fabric/orientation code with special characters stays valid.
  const placeholderSrc = useMemo(() => buildCylindoFrameOneUrl({
    productId,
    features: cylindoFeatures
  }), [cylindoFeatures, productId]);
  useLayoutEffect(() => {
    const viewer_0 = containerRef.current;
    if (!viewer_0) {
      return undefined;
    }
    const handleLoaded = () => {
      const {
        modelViewer
      } = viewer_0;
      if (modelViewer) modelViewer.exposure = 0.9;
      if (onViewerLoaded) onViewerLoaded();
    };
    viewer_0.addEventListener('loaded', handleLoaded);
    return () => viewer_0.removeEventListener('loaded', handleLoaded);
  }, [onViewerLoaded]);

  // Cylindo Avo events (CON-8512). Every gallery variant renders this viewer,
  // so wiring the listeners here covers all of them at once.
  const cylindoPropertiesRef = useRef(productProperties);
  cylindoPropertiesRef.current = productProperties;
  useEffect(() => {
    const viewer_1 = containerRef.current;
    if (!viewer_1) {
      return undefined;
    }
    const cylindoEventProperties = () => {
      const properties = cylindoPropertiesRef.current;
      return {
        category: properties.category,
        fabricName: properties.fabricName,
        fabricId: properties.fabricId,
        sku: properties.sku,
        productFamily: properties.productFamily,
        productId: properties.productId,
        price: properties.price,
        fabricColor: properties.fabricColor,
        name: properties.name,
        variant: properties.variant
      };
    };
    const handleSpinStart = () => avo.cylindoSpinStarted(cylindoEventProperties());
    const handleSpinEnd = () => avo.cylindoSpinEnded(cylindoEventProperties());

    // `zoom-change` streams per frame, so collapse a gesture into one event.
    // `zoom-stopped` only fires on a full reset, so it can't report a zoom in.
    let lastZoom = UNZOOMED_LEVEL;
    let pendingZoomDirection = null;
    let zoomSettleTimeout = null;
    const emitZoomEvent = () => {
      zoomSettleTimeout = null;
      if (pendingZoomDirection === 'in') {
        avo.cylindoZoomedIn(cylindoEventProperties());
      } else if (pendingZoomDirection === 'out') {
        avo.cylindoZoomedOut(cylindoEventProperties());
      }
      pendingZoomDirection = null;
    };
    const handleZoomChange = event => {
      const nextZoom = event?.detail?.zoom;
      if (typeof nextZoom !== 'number' || nextZoom === lastZoom) {
        return;
      }
      pendingZoomDirection = nextZoom > lastZoom ? 'in' : 'out';
      lastZoom = nextZoom;
      clearTimeout(zoomSettleTimeout);
      zoomSettleTimeout = setTimeout(emitZoomEvent, ZOOM_SETTLE_DELAY);
    };

    // Changing fabric reloads the viewer, which resets zoom to UNZOOMED_LEVEL
    // and emits a zoom-change the user never asked for - drop that one.
    const handleViewerContentChange = () => {
      clearTimeout(zoomSettleTimeout);
      zoomSettleTimeout = null;
      pendingZoomDirection = null;
      lastZoom = UNZOOMED_LEVEL;
    };

    // Both variants' AR buttons flip the viewer into AR mode, which is what
    // emits `qr-code`. The iOS Quick Look links bypass it and track themselves.
    const handleArStarted = () => trackGalleryInteracted(GALLERY_INTERACTION.AR_STARTED);
    viewer_1.addEventListener('spin-start', handleSpinStart);
    viewer_1.addEventListener('spin-end', handleSpinEnd);
    viewer_1.addEventListener('zoom-change', handleZoomChange);
    viewer_1.addEventListener('features-change', handleViewerContentChange);
    viewer_1.addEventListener('item-change', handleViewerContentChange);
    viewer_1.addEventListener('qr-code', handleArStarted);
    return () => {
      clearTimeout(zoomSettleTimeout);
      viewer_1.removeEventListener('spin-start', handleSpinStart);
      viewer_1.removeEventListener('spin-end', handleSpinEnd);
      viewer_1.removeEventListener('zoom-change', handleZoomChange);
      viewer_1.removeEventListener('features-change', handleViewerContentChange);
      viewer_1.removeEventListener('item-change', handleViewerContentChange);
      viewer_1.removeEventListener('qr-code', handleArStarted);
    };
  }, [trackGalleryInteracted]);
  return <>
      <Helmet>
        <link rel="preconnect" href="https://content.cylindo.com" />
      </Helmet>
      <div className="w-full !h-full ml-auto mr-auto max-md:min-h-[375px] flex justify-center items-center [&_img]:w-4/5 lg:[&_cylindo-viewer]:aspect-[1.8]" data-xc="PDPCylindo3dViewerContainer">
        <cylindo-viewer controls={controls} ref={containerRef} key={containerId} customer-id={CYLINDO_ACCOUNT_ID} code={productId?.toString()} id={containerId}
      // Without this Cylindo skips rendering its zoom fabs on touch devices, and
      // our +/- buttons have nothing to click. They stay hidden via ::part(fab)
      show-zoom-controls-on-touch="" data-xc="PDPCylindo3dViewer">
          {/* A fallback image when cylindo image is not available */}
          <img slot="placeholder" alt="placeholder" src={placeholderSrc} />
        </cylindo-viewer>
      </div>
    </>;
};
PDPCylindo3dViewer.propTypes = {
  containerId: PropTypes.string,
  cylindoFeatures: PropTypes.objectOf(PropTypes.string),
  isMobile: PropTypes.bool,
  rotateIndex: PropTypes.number,
  onViewerLoaded: PropTypes.func
};
export default PDPCylindo3dViewer;